import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../../components/ui/Button';
import { getUserStatus, getMyAppeals, createAppeal, type UserModerationStatus, type AppealWithDetails, type CreateAppealRequest } from '../../services/moderation';
import { AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';

export function AppealsPage() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [moderationStatus, setModerationStatus] = useState<UserModerationStatus | null>(null);
  const [appeals, setAppeals] = useState<AppealWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAppealForm, setShowAppealForm] = useState(false);
  const [appealText, setAppealText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (user?.username) {
      loadData();
    }
  }, [isAuthenticated, user, navigate]);

  async function loadData() {
    if (!user?.username) return;

    try {
      setLoading(true);
      setError(null);
      const [statusData, appealsData] = await Promise.all([
        getUserStatus(user.username).catch(() => null),
        getMyAppeals().catch(() => []),
      ]);

      setModerationStatus(statusData);
      setAppeals(appealsData || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmitAppeal(e: React.FormEvent) {
    e.preventDefault();

    if (!appealText.trim()) {
      showError('Please enter your appeal text');
      return;
    }

    // Check if user has an active ban or mute and get the corresponding ID
    if (!moderationStatus?.isBanned && !moderationStatus?.isMuted) {
      showError('No active ban or mute found to appeal');
      return;
    }

    // Determine which ID to use - prefer ban ID over mute ID
    const stateId = moderationStatus.banId || moderationStatus.muteId;
    if (!stateId) {
      showError('Unable to find ban or mute record ID');
      return;
    }

    try {
      setSubmitting(true);

      const request: CreateAppealRequest = {
        banId: stateId, // Use the actual ban/mute ID from user_topic_state
        appealText: appealText.trim(),
      };

      await createAppeal(request);
      showSuccess('Appeal submitted successfully');
      setShowAppealForm(false);
      setAppealText('');
      loadData();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to submit appeal');
    } finally {
      setSubmitting(false);
    }
  }

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <div className="text-slate-600 dark:text-slate-400">Loading...</div>
      </div>
    );
  }

  const canAppeal = (moderationStatus?.isBanned || moderationStatus?.isMuted) &&
                     appeals.filter(a => a.appeal.status === 'pending').length === 0;

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Header */}
      <div className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">My Appeals</h1>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Manage your ban and mute appeals
              </p>
            </div>
            <Button onClick={() => navigate('/profile')} variant="secondary">
              Back to Profile
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400 mb-6">
            Error: {error}
          </div>
        )}

        {/* Moderation Status */}
        {moderationStatus && (moderationStatus.isBanned || moderationStatus.isMuted) && (
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-orange-900 dark:text-orange-100 mb-2">
                  Account Status: {moderationStatus.isBanned ? 'Banned' : 'Muted'}
                </h2>

                {moderationStatus.banReason && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-orange-800 dark:text-orange-300 mb-1">Reason:</p>
                    <p className="text-sm text-orange-700 dark:text-orange-400">{moderationStatus.banReason}</p>
                  </div>
                )}

                {moderationStatus.muteReason && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-orange-800 dark:text-orange-300 mb-1">Mute Reason:</p>
                    <p className="text-sm text-orange-700 dark:text-orange-400">{moderationStatus.muteReason}</p>
                  </div>
                )}

                {moderationStatus.isMuted && moderationStatus.mutedUntil && (
                  <p className="text-sm text-orange-600 dark:text-orange-400 mb-3">
                    Muted until: {new Date(moderationStatus.mutedUntil).toLocaleString()}
                  </p>
                )}

                {canAppeal && !showAppealForm && (
                  <Button
                    onClick={() => setShowAppealForm(true)}
                    className="bg-orange-600 hover:bg-orange-700 mt-2"
                  >
                    Submit Appeal
                  </Button>
                )}

                {!canAppeal && appeals.filter(a => a.appeal.status === 'pending').length > 0 && (
                  <p className="text-sm text-orange-700 dark:text-orange-400 italic">
                    You already have a pending appeal. Please wait for it to be reviewed.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Appeal Form */}
        {showAppealForm && (
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              Submit an Appeal
            </h3>

            <form onSubmit={handleSubmitAppeal}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Appeal Text *
                </label>
                <textarea
                  value={appealText}
                  onChange={(e) => setAppealText(e.target.value)}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  rows={6}
                  placeholder="Explain why you believe your ban or mute should be reconsidered..."
                  required
                />
                <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                  Please provide a clear and respectful explanation for your appeal.
                </p>
              </div>

              <div className="flex gap-3">
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Appeal'}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setShowAppealForm(false);
                    setAppealText('');
                  }}
                  variant="secondary"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Appeals List */}
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
            Your Appeals ({appeals.length})
          </h2>

          {appeals.length === 0 ? (
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center">
              <p className="text-slate-600 dark:text-slate-400">
                You have not submitted any appeals yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {appeals.map((item) => (
                <div
                  key={item.appeal.id}
                  className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      {item.appeal.status === 'pending' && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-800 rounded text-xs text-yellow-700 dark:text-yellow-400">
                          <Clock className="w-3 h-3" />
                          Pending
                        </span>
                      )}
                      {item.appeal.status === 'approved' && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-800 rounded text-xs text-green-700 dark:text-green-400">
                          <CheckCircle className="w-3 h-3" />
                          Approved
                        </span>
                      )}
                      {item.appeal.status === 'rejected' && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-400">
                          <XCircle className="w-3 h-3" />
                          Rejected
                        </span>
                      )}
                      <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-xs text-slate-700 dark:text-slate-300">
                        {item.banType === 'banned' ? 'Ban Appeal' : 'Mute Appeal'}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-500">
                      Submitted: {new Date(item.appeal.createdAt).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Original {item.banType} reason:
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/50 p-2 rounded">
                        {item.banReason}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Your appeal:
                      </p>
                      <p className="text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-900/50 p-3 rounded">
                        {item.appeal.appealText}
                      </p>
                    </div>

                    {item.appeal.reviewNotes && (
                      <div>
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Moderator response:
                        </p>
                        <div className={`p-3 rounded ${
                          item.appeal.status === 'approved'
                            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900'
                            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900'
                        }`}>
                          <p className={`text-sm ${
                            item.appeal.status === 'approved'
                              ? 'text-green-800 dark:text-green-300'
                              : 'text-red-800 dark:text-red-300'
                          }`}>
                            {item.appeal.reviewNotes}
                          </p>
                          {item.appeal.reviewedAt && (
                            <p className={`text-xs mt-1 ${
                              item.appeal.status === 'approved'
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            }`}>
                              Reviewed on {new Date(item.appeal.reviewedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* No Moderation Status */}
        {moderationStatus && !moderationStatus.isBanned && !moderationStatus.isMuted && appeals.length === 0 && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900 rounded-lg p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">
              Account in Good Standing
            </h3>
            <p className="text-sm text-green-700 dark:text-green-300">
              You currently have no active bans or mutes, and no pending appeals.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
